import { Rarity, Tag, type ModDef } from '../../sim/mods';
import { Stat } from '../../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Mods « de forme » — huitième famille du pool.

   Le banc d'équilibrage a montré pourquoi ils sont nécessaires. Avec un pool
   trop étroit, chaque Commun apparaissait dans plus de 70 % des builds : ce
   n'est plus un choix, c'est une file d'attente. Élargir le pool est le seul
   vrai remède — aplatir les poids ne fait que déplacer le problème.

   Ceux-ci apportent surtout de la VARIÉTÉ de sensation à DPS comparable. Deux
   builds de même puissance doivent pouvoir se jouer différemment, sinon la
   progression n'est qu'un compteur qui monte.
   ═══════════════════════════════════════════════════════════════════════════ */

const mods: ModDef[] = [
  {
    id: 'canon-jumele',
    name: 'Canon jumelé',
    rarity: Rarity.Rare,
    tags: [Tag.Cinetique, Tag.Drone],
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.13 },
      { stat: Stat.BulletRadius, op: 'mul', value: 0.2 },
    ],
    text: '+13 % de dégâts, +20 % de largeur de tir',
  },
  {
    id: 'noyau-dense',
    name: 'Noyau dense',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma, Tag.Thermique],
    modifiers: [{ stat: Stat.Damage, op: 'mul', value: 0.2 }],
    text: '+20 % de dégâts',
  },
  {
    id: 'refroidissement',
    name: 'Refroidissement actif',
    rarity: Rarity.Common,
    tags: [Tag.Thermique],
    modifiers: [{ stat: Stat.FireRate, op: 'mul', value: 0.13 }],
    text: '+13 % de cadence',
  },
  {
    id: 'servo-vise',
    name: 'Servo de visée',
    rarity: Rarity.Common,
    tags: [Tag.Drone],
    modifiers: [
      { stat: Stat.BulletSpeed, op: 'mul', value: 0.3 },
      { stat: Stat.Damage, op: 'mul', value: 0.08 },
    ],
    text: '+30 % de vitesse de tir, +8 % de dégâts',
  },
  {
    id: 'condensateur',
    name: 'Condensateur',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma, Tag.Surcharge],
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.16 },
      { stat: Stat.FireRate, op: 'mul', value: 0.08 },
    ],
    text: '+16 % de dégâts, +8 % de cadence',
  },
  {
    id: 'blindage-leger',
    name: 'Blindage léger',
    rarity: Rarity.Common,
    tags: [Tag.Orbital],
    modifiers: [
      { stat: Stat.GrazeRadius, op: 'mul', value: 0.18 },
      { stat: Stat.MoveSpeed, op: 'mul', value: 0.07 },
    ],
    text: '+18 % de rayon de frôlement, +7 % de vitesse',
  },
  {
    id: 'ligne-de-tir',
    name: 'Ligne de tir',
    rarity: Rarity.Common,
    tags: [Tag.Cinetique],
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.09 },
      { stat: Stat.BulletSpeed, op: 'mul', value: 0.18 },
    ],
    text: '+9 % de dégâts, +18 % de vitesse de tir',
  },
  {
    id: 'recuperateur',
    name: 'Récupérateur',
    rarity: Rarity.Common,
    tags: [Tag.Drone, Tag.Orbital],
    modifiers: [
      { stat: Stat.CellGain, op: 'mul', value: 0.25 },
      { stat: Stat.GrazeRadius, op: 'mul', value: 0.12 },
    ],
    text: '+25 % de cellules, +12 % de rayon de frôlement',
  },

  /* ── J12 — largeur contre densité ─────────────────────────────────────────
     La question que ces cartes posent : préfère-t-on COUVRIR l'écran ou
     PERFORER une cible ? Les deux réponses doivent rester gagnantes, sinon il
     n'y a qu'une forme de tir viable et la famille entière ne sert à rien.
     ─────────────────────────────────────────────────────────────────────── */
  {
    id: 'gerbe-serree',
    name: 'Gerbe serrée',
    rarity: Rarity.Rare,
    tags: [Tag.Cinetique, Tag.Drone],
    maxStacks: 2,
    // Multiplicatif sur le nombre de projectiles : sans volée déjà large, les
    // +25 % disparaissent dans l'arrondi. Voir « Essaim dense ».
    modifiers: [
      { stat: Stat.BulletCount, op: 'mul', value: 0.2 },
      { stat: Stat.Damage, op: 'mul', value: 0.08 },
      { stat: Stat.BulletRadius, op: 'mul', value: -0.12 },
    ],
    text: '+20 % de projectiles (arrondi à l’inférieur), +8 % de dégâts, −12 % de largeur de tir',
  },
  {
    id: 'nappe-large',
    name: 'Nappe large',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma, Tag.Drone],
    maxStacks: 2,
    modifiers: [
      { stat: Stat.BulletCount, op: 'mul', value: 0.16 },
      { stat: Stat.Damage, op: 'mul', value: 0.08 },
      { stat: Stat.BulletSpeed, op: 'mul', value: -0.2 },
    ],
    text: '+16 % de projectiles (arrondi à l’inférieur), +8 % de dégâts, −20 % de vitesse de tir',
  },
  {
    id: 'canon-de-precision',
    name: 'Canon de précision',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma, Tag.Cinetique],
    maxStacks: 2,
    // L'autre bout de la famille : un tir plus étroit rate davantage, donc le
    // gain affiché est un plafond, pas une promesse. Le pendant exact de
    // « Faisceau large », qui échange l'inverse.
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.34 },
      { stat: Stat.BulletRadius, op: 'mul', value: -0.22 },
    ],
    text: '+34 % de dégâts, −22 % de largeur de tir',
  },
  {
    id: 'noyau-perforant',
    name: 'Noyau perforant',
    rarity: Rarity.Common,
    tags: [Tag.Cinetique],
    maxStacks: 3,
    // ADDITIF sur les dégâts, seul du pool à l'être. Conséquence utile : sa
    // valeur ne dépend pas des multiplicateurs déjà pris, seulement des autres
    // exemplaires de lui-même — +20 %, puis +17 %, puis +14 %. Il donne au
    // joueur une carte dont le rendement décroît VISIBLEMENT, ce qui est une
    // raison légitime de refuser la troisième.
    modifiers: [{ stat: Stat.Damage, op: 'add', value: 2 }],
    text: '+2 dégâts par projectile',
  },
  {
    id: 'dispersion-controlee',
    name: 'Dispersion contrôlée',
    rarity: Rarity.Common,
    tags: [Tag.Thermique, Tag.Orbital],
    maxStacks: 2,
    modifiers: [
      { stat: Stat.BulletRadius, op: 'mul', value: 0.3 },
      { stat: Stat.FireRate, op: 'mul', value: 0.08 },
      { stat: Stat.MoveSpeed, op: 'mul', value: -0.08 },
    ],
    text: '+30 % de largeur de tir, +8 % de cadence, −8 % de vitesse de déplacement',
  },
];

export default mods;
